/**
 * Event Stream — the system-wide auditable event feed (frontend-display.md §9.1,
 * frontend-entity-coverage.md §5, frontend-ux-design.md §6).
 *
 * The canonical invariant "all events are auditable" gets a dedicated, navigable
 * home (not just a Refine resource list). This screen adds, over the existing
 * append-only `events` log:
 *   - a Filter bar (event_type · source · status · correlation_id · date range);
 *   - Correlation grouping (collapse events sharing a correlation_id into one
 *     expandable thread — the saga / causation view);
 *   - Live tail (poll newest-first with a pause toggle; no websocket);
 *   - a per-row Replay action (POST /api/events/:id/replay, disabled when
 *     non_replayable);
 *   - deep-links into the affected entity, the event show, and correlation
 *     siblings.
 *
 * No new API: server-supported `eq` filters (event_type / source / status) and
 * `q` (correlation_id) map onto the worker's `<field>=<value>` + `q` query
 * params via the dataProvider; the date range — which the v0.1 worker's flat
 * query params don't express — is applied client-side over the fetched window
 * and labeled as such, so the screen degrades but never hard-errors.
 */
import * as React from "react";
import { useList, useNavigation } from "@refinedev/core";
import {
  ChevronDown,
  ChevronRight,
  GitBranch,
  Pause,
  Play,
  RotateCcw,
  X,
} from "@/components/ui/icon";
import {
  EVENT_STATUS,
  EVENT_TYPES,
  type Event,
  type EventStatus,
  type EventType,
} from "@aos/shared";

import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/layout/PageHeader";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/app/StatusBadge";
import { RefLink } from "@/components/inspector";
import { useApiAction } from "@/resources/_shared/useApiAction";
import { formatDateTime, orDash } from "@/resources/_shared/runs-ui";
import { humanize } from "@/lib/format";

/** "All" sentinel for the Select filters (Radix Select forbids empty values). */
const ALL = "__all__";

/** How long the live-tail poll waits between refetches. */
const LIVE_TAIL_INTERVAL_MS = 10_000;

/** Window the server returns; date range + grouping are applied over this set. */
const PAGE_SIZE = 100;

interface EventFilters {
  eventType: EventType | typeof ALL;
  source: string;
  status: EventStatus | typeof ALL;
  correlationId: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: EventFilters = {
  eventType: ALL,
  source: "",
  status: ALL,
  correlationId: "",
  from: "",
  to: "",
};

export function EventStream() {
  const { show } = useNavigation();
  const [filters, setFilters] = React.useState<EventFilters>(EMPTY_FILTERS);
  const [grouped, setGrouped] = React.useState(false);
  const [liveTail, setLiveTail] = React.useState(false);

  // Server-supported filters: `eq` on event_type/source/status, `q` for the
  // correlation_id free-text search. Unsupported predicates (date range) are
  // applied client-side below.
  const serverFilters = React.useMemo(() => {
    const list: { field: string; operator: "eq" | "contains"; value: string }[] =
      [];
    if (filters.eventType !== ALL)
      list.push({ field: "event_type", operator: "eq", value: filters.eventType });
    if (filters.source.trim())
      list.push({ field: "source", operator: "eq", value: filters.source.trim() });
    if (filters.status !== ALL)
      list.push({ field: "status", operator: "eq", value: filters.status });
    if (filters.correlationId.trim())
      list.push({ field: "q", operator: "contains", value: filters.correlationId.trim() });
    return list;
  }, [filters]);

  const { data, isLoading, isError, refetch, isFetching } = useList<Event>({
    resource: "events",
    pagination: { pageSize: PAGE_SIZE },
    sorters: [{ field: "created_at", order: "desc" }],
    filters: serverFilters,
    queryOptions: {
      retry: false,
      // Live tail: poll newest-first while enabled (no websocket, §9.1).
      refetchInterval: liveTail ? LIVE_TAIL_INTERVAL_MS : false,
    },
  });

  const allEvents = React.useMemo(() => data?.data ?? [], [data]);

  // Client-side date-range filter (the v0.1 worker can't express gt/lt).
  const events = React.useMemo(() => {
    const fromTs = filters.from ? Date.parse(filters.from) : null;
    const toTs = filters.to ? Date.parse(filters.to) : null;
    if (fromTs === null && toTs === null) return allEvents;
    return allEvents.filter((e) => {
      const ts = Date.parse(e.created_at);
      if (Number.isNaN(ts)) return true;
      if (fromTs !== null && ts < fromTs) return false;
      if (toTs !== null && ts > toTs) return false;
      return true;
    });
  }, [allEvents, filters.from, filters.to]);

  const hasFilters =
    filters.eventType !== ALL ||
    filters.source.trim() !== "" ||
    filters.status !== ALL ||
    filters.correlationId.trim() !== "" ||
    filters.from !== "" ||
    filters.to !== "";

  const dateRangeActive = filters.from !== "" || filters.to !== "";

  return (
    <div className="space-y-6" data-testid="event-stream">
      {/* Page header. */}
      <PageHeader
        title="Event Stream"
        subtitle="The system-wide audit feed. Every state mutation emits an event — filter, correlate, and replay."
        icon="events"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setGrouped((g) => !g)}
              aria-pressed={grouped}
              data-testid="event-stream-group-toggle"
            >
              <GitBranch className="h-4 w-4" />
              {grouped ? "Ungroup" : "Group by saga"}
            </Button>
            <Button
              variant={liveTail ? "default" : "outline"}
              size="sm"
              onClick={() => setLiveTail((v) => !v)}
              aria-pressed={liveTail}
              data-testid="event-stream-live-toggle"
            >
              {liveTail ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              {liveTail ? "Pause" : "Live tail"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              data-testid="event-stream-refresh"
            >
              <RotateCcw className="h-4 w-4" />
              {isFetching ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        }
      />

      {/* Filter bar — no card; quiet controls on the page paper. */}
      <FilterBar
        filters={filters}
        onChange={setFilters}
        hasFilters={hasFilters}
      />

      {dateRangeActive && (
        <p className="text-xs text-muted-foreground">
          Date range is applied to the latest {PAGE_SIZE} events client-side
          (the v0.1 events API has no range query).
        </p>
      )}

      {/* Body. */}
      {isError ? (
        <div data-testid="event-stream-error">
          <ErrorState
            message="The events API is unreachable. Try refreshing."
            onRetry={() => refetch()}
          />
        </div>
      ) : isLoading ? (
        <EventTableSkeleton />
      ) : events.length === 0 ? (
        <div data-testid="event-stream-empty">
          <EmptyState
            icon="events"
            title="No events"
            hint={
              hasFilters
                ? "No events match the current filters."
                : "Nothing has been recorded yet. Events appear here as the system runs."
            }
            action={
              hasFilters ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters(EMPTY_FILTERS)}
                >
                  <X className="h-4 w-4" />
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : grouped ? (
        <GroupedView
          events={events}
          onShow={(id) => show("events", id)}
          onCorrelate={(cid) =>
            setFilters((f) => ({ ...f, correlationId: cid }))
          }
        />
      ) : (
        <FlatView
          events={events}
          onShow={(id) => show("events", id)}
          onCorrelate={(cid) =>
            setFilters((f) => ({ ...f, correlationId: cid }))
          }
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Filter bar
 * ────────────────────────────────────────────────────────────────────────── */

function FilterBar({
  filters,
  onChange,
  hasFilters,
}: {
  filters: EventFilters;
  onChange: React.Dispatch<React.SetStateAction<EventFilters>>;
  hasFilters: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3" data-testid="event-stream-filters">
      <FilterField label="Type">
        <Select
          value={filters.eventType}
          onValueChange={(v) =>
            onChange((f) => ({ ...f, eventType: v as EventType | typeof ALL }))
          }
        >
          <SelectTrigger className="w-[200px]" aria-label="Filter by event type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            {EVENT_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {humanize(t)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      <FilterField label="Source">
        <Input
          value={filters.source}
          onChange={(e) => onChange((f) => ({ ...f, source: e.target.value }))}
          placeholder="e.g. kernel"
          className="w-[160px]"
          aria-label="Filter by source"
        />
      </FilterField>

      <FilterField label="Status">
        <Select
          value={filters.status}
          onValueChange={(v) =>
            onChange((f) => ({ ...f, status: v as EventStatus | typeof ALL }))
          }
        >
          <SelectTrigger className="w-[160px]" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {EVENT_STATUS.map((s) => (
              <SelectItem key={s} value={s}>
                {humanize(s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      <FilterField label="Correlation id">
        <Input
          value={filters.correlationId}
          onChange={(e) =>
            onChange((f) => ({ ...f, correlationId: e.target.value }))
          }
          placeholder="saga id…"
          className="w-[200px] font-mono text-xs"
          aria-label="Filter by correlation id"
        />
      </FilterField>

      <FilterField label="From">
        <Input
          type="datetime-local"
          value={filters.from}
          onChange={(e) => onChange((f) => ({ ...f, from: e.target.value }))}
          className="w-[200px]"
          aria-label="From date"
        />
      </FilterField>

      <FilterField label="To">
        <Input
          type="datetime-local"
          value={filters.to}
          onChange={(e) => onChange((f) => ({ ...f, to: e.target.value }))}
          className="w-[200px]"
          aria-label="To date"
        />
      </FilterField>

      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange(EMPTY_FILTERS)}
          data-testid="event-stream-clear-filters"
        >
          <X className="h-4 w-4" />
          Clear
        </Button>
      )}
    </div>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      {children}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Flat view — one DataTable-style grid (the true-grid border is rule 3).
 * ────────────────────────────────────────────────────────────────────────── */

interface RowActions {
  onShow: (id: string) => void;
  onCorrelate: (correlationId: string) => void;
}

function FlatView({ events, ...actions }: { events: Event[] } & RowActions) {
  return (
    <div className="rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>Artifact</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Correlation</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => (
            <EventRow key={event.id} event={event} {...actions} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function EventRow({
  event,
  onShow,
  onCorrelate,
}: { event: Event } & RowActions) {
  return (
    <TableRow data-testid="event-row">
      <TableCell>
        <button
          type="button"
          className="font-medium hover:underline"
          onClick={() => onShow(event.id)}
        >
          {humanize(event.event_type)}
        </button>
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <RefLink resource="artifacts" id={event.artifact_id} />
      </TableCell>
      <TableCell className="text-sm">{orDash(event.source)}</TableCell>
      <TableCell>
        <StatusBadge status={event.status} />
      </TableCell>
      <TableCell>
        {event.correlation_id ? (
          <button
            type="button"
            className="font-mono text-xs text-muted-foreground hover:underline"
            onClick={() => onCorrelate(event.correlation_id!)}
            title="Show all events in this saga"
          >
            {shortId(event.correlation_id)}
          </button>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
        {formatDateTime(event.created_at)}
      </TableCell>
      <TableCell className="text-right">
        <ReplayButton event={event} />
      </TableCell>
    </TableRow>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Grouped (saga) view — collapse events sharing a correlation_id.
 * ────────────────────────────────────────────────────────────────────────── */

interface Saga {
  key: string;
  correlationId: string | null;
  events: Event[];
  latest: string;
}

function GroupedView({
  events,
  ...actions
}: { events: Event[] } & RowActions) {
  const sagas = React.useMemo<Saga[]>(() => {
    const map = new Map<string, Saga>();
    for (const e of events) {
      // Events without a correlation_id stand alone (keyed by their own id).
      const key = e.correlation_id ?? `solo:${e.id}`;
      const existing = map.get(key);
      if (existing) {
        existing.events.push(e);
        if (e.created_at > existing.latest) existing.latest = e.created_at;
      } else {
        map.set(key, {
          key,
          correlationId: e.correlation_id,
          events: [e],
          latest: e.created_at,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.latest < b.latest ? 1 : -1,
    );
  }, [events]);

  return (
    <div className="space-y-2" data-testid="event-stream-grouped">
      {sagas.map((saga) => (
        <SagaGroup key={saga.key} saga={saga} {...actions} />
      ))}
    </div>
  );
}

function SagaGroup({ saga, ...actions }: { saga: Saga } & RowActions) {
  const [open, setOpen] = React.useState(true);
  const isSolo = saga.correlationId === null;
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="rounded-md border border-border" data-testid="event-saga">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent/50"
        aria-expanded={open}
      >
        <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" />
        <GitBranch className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="font-mono text-xs text-muted-foreground">
          {isSolo ? "uncorrelated" : shortId(saga.correlationId!)}
        </span>
        <span className="text-sm text-muted-foreground">
          {saga.events.length} event{saga.events.length === 1 ? "" : "s"}
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          {formatDateTime(saga.latest)}
        </span>
      </button>
      {open && (
        <Table>
          <TableBody>
            {saga.events
              .slice()
              .sort((a, b) => (a.created_at < b.created_at ? -1 : 1))
              .map((event) => (
                <EventRow key={event.id} event={event} {...actions} />
              ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Replay action — POST /api/events/:id/replay (disabled when non_replayable).
 * ────────────────────────────────────────────────────────────────────────── */

function ReplayButton({ event }: { event: Event }) {
  const { run, loading } = useApiAction();
  const canReplay = event.status !== "non_replayable";

  const replay = (e: React.MouseEvent) => {
    e.stopPropagation();
    void run(`/events/${event.id}/replay`, {
      invalidateResources: ["events", "rule-runs", "agent-runs"],
      successMessage: "Event re-emitted through the kernel.",
    });
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={replay}
      disabled={!canReplay || loading}
      title={
        canReplay ? "Replay this event" : "This event is marked non_replayable"
      }
      data-testid="event-replay"
    >
      <RotateCcw className="h-4 w-4" />
      {loading ? "Replaying…" : "Replay"}
    </Button>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────────────────── */

function EventTableSkeleton() {
  return (
    <div className="rounded-md border border-border p-3" data-testid="event-stream-loading">
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    </div>
  );
}

/** Compact a long uuid for the dense correlation column. */
function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

export default EventStream;
